import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Chip,
    alpha,
    useTheme,
    TextField,
    Collapse,
    Grid,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Button,
    Radio,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    DragIndicator as DragIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import { useSessionStudio } from './SessionContext';
import { Problem, ProblemOption } from 'services/sessionService';
import RichTextDisplay from 'components/common/RichTextDisplay';
import RichTextEditor from 'components/forms/RichTextEditor';
import { normalizeImagePath, uploadImage } from 'utils/imageUtils';

interface ProblemItemProps {
    problem: Problem;
    isLast: boolean;
}

const ProblemItem: React.FC<ProblemItemProps> = ({ problem, isLast }) => {
    const theme = useTheme();
    const { updateProblem, deleteProblem } = useSessionStudio();
    const [isExpanded, setIsExpanded] = useState(false);
    const [statement, setStatement] = useState(problem.problemStatement);
    const [correctSolution, setCorrectSolution] = useState(problem.correctSolution || '');
    const [difficultyLevel, setDifficultyLevel] = useState(problem.difficultyLevel);
    const [problemType, setProblemType] = useState(problem.problemType);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [options, setOptions] = useState<ProblemOption[]>(
        problem.options || [
            { id: 'temp-1', optionText: '', isCorrect: true, optionOrder: 1 },
            { id: 'temp-2', optionText: '', isCorrect: false, optionOrder: 2 },
            { id: 'temp-3', optionText: '', isCorrect: false, optionOrder: 3 },
            { id: 'temp-4', optionText: '', isCorrect: false, optionOrder: 4 }
        ]
    );

    // Sync local state when the underlying problem changes (e.g. after refresh).
    useEffect(() => {
        setStatement(problem.problemStatement);
    }, [problem.problemStatement]);

    useEffect(() => {
        if (problem.options && problem.options.length > 0) {
            setOptions(problem.options);
        }
    }, [problem.options]);

    const handleSaveStatement = async () => {
        if (statement !== problem.problemStatement) {
            try {
                await updateProblem(problem.id, { problemStatement: statement });
            } catch (error) {
                console.error('Failed to update problem statement:', error);
                setStatement(problem.problemStatement);
            }
        }
    };

    const handleSaveSolution = async () => {
        if (correctSolution !== problem.correctSolution) {
            try {
                await updateProblem(problem.id, { correctSolution });
            } catch (error) {
                console.error('Failed to update solution:', error);
                setCorrectSolution(problem.correctSolution || '');
            }
        }
    };

    const handleSaveDifficulty = async (newDifficulty: number) => {
        try {
            await updateProblem(problem.id, { difficultyLevel: newDifficulty });
            setDifficultyLevel(newDifficulty);
        } catch (error) {
            console.error('Failed to update difficulty:', error);
            setDifficultyLevel(problem.difficultyLevel);
        }
    };

    const handleSaveProblemType = async (newType: 'open_ended' | 'multiple_choice') => {
        try {
            await updateProblem(problem.id, { problemType: newType });
            setProblemType(newType);
        } catch (error) {
            console.error('Failed to update problem type:', error);
            setProblemType(problem.problemType);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setUploadError(null);
        try {
            const filePath = await uploadImage(file);
            await updateProblem(problem.id, { imagePath: filePath });
        } catch (error) {
            const err = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
            setUploadError(
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to upload image'
            );
        } finally {
            setUploading(false);
            // Reset the input so re-selecting the same file fires onChange again.
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = async () => {
        setUploadError(null);
        try {
            await updateProblem(problem.id, { imagePath: null });
        } catch (error) {
            console.error('Failed to remove image:', error);
        }
    };

    const handleOptionTextChange = (index: number, text: string) => {
        const newOptions = [...options];
        newOptions[index] = { ...newOptions[index], optionText: text };
        setOptions(newOptions);
    };

    const handleCorrectOptionChange = (index: number) => {
        const newOptions = options.map((opt, i) => ({
            ...opt,
            isCorrect: i === index
        }));
        setOptions(newOptions);
    };

    const handleAddOption = () => {
        if (options.length >= 6) return;
        setOptions([
            ...options,
            {
                id: `temp-${Date.now()}`,
                optionText: '',
                isCorrect: false,
                optionOrder: options.length + 1
            }
        ]);
    };

    const handleRemoveOption = (index: number) => {
        if (options.length <= 2) return;
        const newOptions = options.filter((_, i) => i !== index).map((opt, i) => ({
            ...opt,
            optionOrder: i + 1
        }));
        // If we removed the correct option, make the first one correct
        if (!newOptions.some(o => o.isCorrect) && newOptions.length > 0) {
            newOptions[0].isCorrect = true;
        }
        setOptions(newOptions);
    };

    const handleSaveOptions = async () => {
        try {
            const correctOption = options.find(o => o.isCorrect);
            await updateProblem(problem.id, {
                options: options.filter(o => o.optionText.trim()),
                correctSolution: correctOption?.optionText || ''
            });
        } catch (error) {
            console.error('Failed to save options:', error);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this problem?')) {
            try {
                await deleteProblem(problem.id);
            } catch (error) {
                console.error('Failed to delete problem:', error);
            }
        }
    };

    const imageUrl = problem.imagePath ? normalizeImagePath(problem.imagePath) : '';

    return (
        <Box
            sx={{
                borderBottom: isLast ? 'none' : '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            {/* Collapsed View */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    pl: 2,
                    '&:hover': {
                        bgcolor: theme.palette.action.hover,
                        '& .action-buttons': { opacity: 1 }
                    }
                }}
            >
                <IconButton size="small" sx={{ cursor: 'grab', mr: 1, opacity: 0.5 }}>
                    <DragIcon fontSize="small" />
                </IconButton>

                {imageUrl && (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt="Problem"
                        sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1,
                            mr: 1.5,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    />
                )}

                <Box sx={{ flexGrow: 1, mr: 2 }}>
                    <Box
                        onClick={() => setIsExpanded(true)}
                        sx={{
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                    >
                        <RichTextDisplay
                            content={problem.problemStatement}
                            variant="body2"
                            fontWeight="medium"
                            preview
                            maxLength={140}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Chip
                            label={problemType === 'multiple_choice' ? 'Multiple Choice' : 'Open Ended'}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Difficulty: {difficultyLevel} • Points: {difficultyLevel * 10}
                        </Typography>
                        {correctSolution && (
                            <Chip
                                label={`Answer: ${correctSolution}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                        )}
                    </Box>
                </Box>

                <Box className="action-buttons" sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" color="error" onClick={handleDelete}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Expanded View - Edit Details */}
            <Collapse in={isExpanded}>
                <Box sx={{ p: 3, pl: 7, bgcolor: alpha(theme.palette.background.default, 0.5), borderTop: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={2}>
                        {/* Problem Statement (rich text) */}
                        <Grid item xs={12}>
                            <RichTextEditor
                                label="Problem Statement"
                                value={statement}
                                onChange={setStatement}
                                onBlur={handleSaveStatement}
                                rows={3}
                            />
                        </Grid>

                        {/* Image */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Image (optional)
                            </Typography>
                            {uploadError && (
                                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError(null)}>
                                    {uploadError}
                                </Alert>
                            )}
                            {imageUrl ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box
                                        component="img"
                                        src={imageUrl}
                                        alt="Problem"
                                        sx={{
                                            maxWidth: 240,
                                            maxHeight: 180,
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleRemoveImage}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    component="label"
                                    startIcon={uploading ? <CircularProgress size={16} /> : <ImageIcon />}
                                    disabled={uploading}
                                >
                                    {uploading ? 'Uploading...' : 'Upload Image'}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png, image/jpeg, image/gif"
                                        hidden
                                        onChange={handleImageUpload}
                                    />
                                </Button>
                            )}
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Problem Type</InputLabel>
                                <Select
                                    value={problemType}
                                    label="Problem Type"
                                    onChange={(e) => handleSaveProblemType(e.target.value as 'open_ended' | 'multiple_choice')}
                                >
                                    <MenuItem value="open_ended">Open Ended</MenuItem>
                                    <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Difficulty Level</InputLabel>
                                <Select
                                    value={difficultyLevel}
                                    label="Difficulty Level"
                                    onChange={(e) => handleSaveDifficulty(Number(e.target.value))}
                                >
                                    <MenuItem value={1}>1 - Easy</MenuItem>
                                    <MenuItem value={2}>2 - Medium</MenuItem>
                                    <MenuItem value={3}>3 - Hard</MenuItem>
                                    <MenuItem value={4}>4 - Very Hard</MenuItem>
                                    <MenuItem value={5}>5 - Expert</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Open Ended: Correct Solution */}
                        {problemType === 'open_ended' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Correct Answer/Solution"
                                    value={correctSolution}
                                    onChange={(e) => setCorrectSolution(e.target.value)}
                                    onBlur={handleSaveSolution}
                                    size="small"
                                    helperText="The correct answer students should submit"
                                />
                            </Grid>
                        )}

                        {/* Multiple Choice: Options */}
                        {problemType === 'multiple_choice' && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                    Answer Options (select the correct one)
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {options.map((option, index) => (
                                        <Paper
                                            key={option.id}
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                bgcolor: option.isCorrect ? alpha(theme.palette.success.main, 0.08) : 'transparent',
                                                borderColor: option.isCorrect ? 'success.main' : 'divider'
                                            }}
                                        >
                                            <Radio
                                                checked={option.isCorrect}
                                                onChange={() => handleCorrectOptionChange(index)}
                                                color="success"
                                                size="small"
                                            />
                                            <TextField
                                                value={option.optionText}
                                                onChange={(e) => handleOptionTextChange(index, e.target.value)}
                                                onBlur={handleSaveOptions}
                                                placeholder={`Option ${index + 1}`}
                                                size="small"
                                                fullWidth
                                                variant="standard"
                                                InputProps={{
                                                    disableUnderline: true
                                                }}
                                            />
                                            {options.length > 2 && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        handleRemoveOption(index);
                                                        // Save after removal
                                                        setTimeout(handleSaveOptions, 100);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Paper>
                                    ))}
                                    {options.length < 6 && (
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddOption}
                                            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                        >
                                            Add Option
                                        </Button>
                                    )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Click the radio button to mark the correct answer
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Collapse>
        </Box>
    );
};

export default ProblemItem;
